import React from "react";
import { NoDecimalFormat, SingleDecimalFormat, TwoDecimalFormat } from "@components/Format";
import moment from "moment";
import { Tag, Popconfirm } from 'antd';
import { api } from "@/redux/api";
import { EventName, emitEvent } from "@/hooks/useEventBus";
import { useSmartNavigate } from '@/hooks/use-smart-navigate';
import { buildFacilityPath, getFacilityContext } from "@/utils/route-context";
import { useSelector } from "react-redux";

export const ElectricityProps = {
    name: 'electricity',
    title: 'Electricity',
    description: 'Saved user operation filters'
}

const GotoElecDetail = ({ id, label }) => {
    const { navigateAndCloseTabs } = useSmartNavigate();
    const facilityContext = useSelector((state) => getFacilityContext(state));
    const handleClick = (e) => {
        e.stopPropagation();
        const nextPath = buildFacilityPath(`elec-detail/${id}`, facilityContext);
        if (!nextPath) return;
        navigateAndCloseTabs(nextPath, { viewType: "facility", leafPrefix: "elec-detail/" });
    };
    return label
        ? <a onClick={handleClick}>{label}</a>
        : <a onClick={handleClick}><i className="fas fa-expand-arrows-alt text-lg"></i></a>;
}

// 定义状态显示组件
const StatusDisplay = ({ is_eui_certified }) => {
    if (is_eui_certified === true) {
        return <Tag color="green">Certified</Tag>;
    } else {
        return <Tag color="red">Uncertified</Tag>;
    }
};

// 定义行为按钮组件
const ActionButtons = ({ record, is_eui_certified, onUpdateCertification }) => {
    const [loading, setLoading] = React.useState(false);

    const handleCertificationChange = async (newStatus) => {
        if (onUpdateCertification) {
            setLoading(true);
            try {
                await onUpdateCertification(record.id, newStatus);
            } finally {
                setLoading(false);
            }
        }
    };

    if (loading) {
        return (
            <span
                onClick={(e) => e.stopPropagation()}
                style={{ 
                    color: '#1890ff',
                    cursor: 'not-allowed',
                    fontSize: '16px'
                }}
            >
                <i className="fas fa-spinner fa-spin" style={{ color: '#1890ff', fontSize: '150%' }}></i>
            </span>
        );
    }

    if (is_eui_certified === true) {
        return (
            <Popconfirm
                title="Uncertify"
                description="Are you sure you want to uncertify this record?"
                onConfirm={() => handleCertificationChange(false)}
                okText="Yes"
                cancelText="No"
                placement="topRight"
            >
                <span
                    onClick={(e) => e.stopPropagation()}
                    style={{ 
                        color: '#1890ff !important',
                        cursor: 'pointer',
                        fontSize: '16px'
                    }}
                    title="Uncertify"
                >
                    <i className="fas fa-times" style={{ color: '#1890ff', fontSize: '150%' }}></i>
                </span>
            </Popconfirm>
        );
    } else {
        return (
            <Popconfirm
                title="Certify"
                description="Are you sure you want to certify this record?"
                onConfirm={() => handleCertificationChange(true)}
                okText="Yes"
                cancelText="No"
                placement="topRight"
            >
                <span
                    onClick={(e) => e.stopPropagation()}
                    style={{ 
                        color: '#1890ff !important',
                        cursor: 'pointer',
                        fontSize: '16px'
                    }}
                    title="Certify"
                >
                    <i className="fas fa-check" style={{ color: '#1890ff', fontSize: '150%' }}></i>
                </span>
            </Popconfirm>
        );
    }
};

export const ElectricityColumns = (refresh, isAssignTo = false, editable = false) => [
    {
        title: 'key',
        dataIndex: 'id',
        key: 'id',
        editable: false,
        search: false,
        importIngore: true,
        hideInTable: true,
        width: '4%'
    },
    {
        title: 'StartDate',
        dataIndex: 'service_start_date',
        key: 'service_start_date',
        valueType: 'date',
        width: '100px',
        required: true,
        search: false,
        editable: true,
        align: 'right', // 时间列右对齐
        formItemProps: {
            rules: [
                {
                    required: true,
                    message: 'required',
                },
            ],
        },
        sorter: (a, b) => new Date(a.service_start_date) - new Date(b.service_start_date)
    },
    {
        title: 'EndDate',
        dataIndex: 'end_start_date',
        key: 'end_start_date',
        width: '100px',
        valueType: 'date',
        required: true,
        search: false,
        editable: true,
        align: 'right', // 时间列右对齐
        formItemProps: {
            rules: [
                {
                    required: true,
                    message: 'required',
                },
            ],
        },
        sorter: (a, b) => new Date(a.end_start_date) - new Date(b.end_start_date)
    },
    {
        title: 'ServiceMonth',
        dataIndex: 'service_month',
        key: 'service_month',
        valueType: 'date',
        width: '100px',
        search: false,
        editable: true,
        align: 'right', // 时间列右对齐
        formItemProps: {
            rules: [
                {
                    required: true,
                    message: 'required',
                },
            ],
        },
        sorter: (a, b) => new Date(a.service_month) - new Date(b.service_month),
        render: (text, record) => (
            <GotoElecDetail id={record.id} label={text} />
        )
    },
    {
        title: <div className={'text-right'}>Days</div>,
        dataIndex: 'service_date',
        key: 'service_date',
        width: '50px',
        editable: true,
        align: 'right', // 数字列右对齐
        sorter: (a, b) => a.service_date - b.service_date,
        render: text => <div
            className={text < 15 ? 'text-right text-red-500' : 'text-right'}>{NoDecimalFormat(text)}</div>,
        renderFormItem: () => ''
    },
    {
        title: <div className={'text-right'}>Usage (kWh)</div>,
        dataIndex: 'usage',
        width: '100px',
        required: true,
        key: 'usage',
        align: 'right', // 数字列右对齐
        formItemProps: {
            rules: [
                {
                    required: true,
                    message: 'required',
                },
            ],
        },
        edit: true,
        sorter: (a, b) => a.usage - b.usage,
        render: (text, record) => (
            <div className={text < 15 ? 'text-right text-red-500' : 'text-right'}>
                {NoDecimalFormat(text)}
            </div>
        )
    },
    {
        title: <div className={'text-right'}>Avg. OAT(°F)</div>,
        dataIndex: 'avgoat',
        key: 'avgoat',
        width: '100px',
        required: false,
        edit: true,
        align: 'right', // 数字列右对齐
        sorter: (a, b) => a.avgoat - b.avgoat,
        render: text => <div
            className={text < 15 ? 'text-right text-red-500' : 'text-right'}>{SingleDecimalFormat(text)}</div>
    },
    {
        title: <div className={'text-right'}>Costs($)</div>,
        dataIndex: 'costs',
        key: 'costs',
        required: true,
        width: '100px',
        edit: true,
        align: 'right', // 数字列右对齐
        formItemProps: {
            rules: [
                {
                    required: true,
                    message: 'required',
                },
            ],
        },
        hideInTable: window.innerWidth < 760,
        sorter: (a, b) => a.costs - b.costs,
        render: text => <div
            className={text < 15 ? 'text-right text-red-500' : 'text-right'}>{TwoDecimalFormat(text)}</div>
    },
    {
        title: <div className={'text-right'}>Adjustment(KWH)</div>,
        dataIndex: 'adjustment',
        key: 'adjustment',
        width: '100px',
        edit: true,
        hideInTable: window.innerWidth < 760,
        align: 'right', // 数字列右对齐
        sorter: (a, b) => a.adjustment - b.adjustment,
        render: text => <div
            className={text < 15 ? 'text-right text-red-500' : 'text-right'}>{NoDecimalFormat(text)}</div>
    },
    {
        title: 'Modified Date',
        dataIndex: 'modified_date',
        key: 'modified_date',
        editable: true,
        width: '100px',
        importIngore: true,
        hideInTable: window.innerWidth < 760,
        align: 'right', // 时间列右对齐
        sorter: (a, b) => a.modified_date - b.modified_date,
        render: (text) => (
            moment(text).format('YYYY-MM-DD')
        ),
        renderFormItem: () => ''
    },
    {
        title: 'Status',
        dataIndex: 'is_eui_certified',
        key: 'is_eui_certified',
        width: 100,
        editable: false,
        align: 'left', // 状态列左对齐
        render: (is_eui_certified) => (
            <StatusDisplay is_eui_certified={is_eui_certified} />
        )
    },
    // 只有当isAssignTo为true时才显示action列
    ...(isAssignTo ? [{
        title: '',
        dataIndex: 'is_eui_certified',
        key: 'action',
        width: 30,
        editable: false,
        render: (is_eui_certified, record) => (
            <ActionButtons
                record={record}
                is_eui_certified={is_eui_certified}
                onUpdateCertification={(id, newStatus) => {
                    return api.updateEuiCertified('elec', {
                        ids: [id],
                        certified: newStatus
                    }).then(res => {
                        refresh()
                        emitEvent(EventName.UPDATE_CERTIFICATION_STATUS)
                        emitEvent(EventName.REFRESH_CERTIFY_REQUEST)
                    })
                }}
            />
        )
    }] : []),
    {
        title: '',
        valueType: 'option',
        width: '8rem',
        importIngore: true,
        render: (text, record, _, action) => (
            <div className={'space-around'}>
                {editable && <a
                    key="editable"
                    onClick={(e) => {
                        e.stopPropagation();
                        console.log('test')
                        return (action?.startEditable?.(record.id))
                    }}
                >
                    <i className="fas fa-edit text-lg"></i>
                </a>}

                {/* <GotoElecDetail id={record.id} /> */}
            </div>
        ),
    },
];
